import React, { createContext, Key, ReactNode, useCallback, useContext, useState } from "react";
import { isEmpty } from "lodash";
import { GetStatementTitleListParams, PaginatedApiParams, StatementTitleParams } from "../types/reconciliation";

interface TitlesTableContextType {
  page: number;
  pageSize: number;
  ordering: string | undefined;
  broker: Key[];
  lob: Key[];
  policyNumber: Key[];
  ecNumber: Key[];
  ecPeriod: Key[];
  policyEffectiveDate: Key[];
  policyExpiringDate: Key[];
  titleEffectiveDate: Key[];
  titleReceiptDate: Key[];
  credit: Key[];
  creditDelta: Key[];
  creditDifference: Key[];
  debit: Key[];
  debitDelta: Key[];
  creditStatus: Key[];
  debitStatus: Key[];
  debitDifference: Key[];
  creditDebitStatus: Key[];
  contractor: Key[];
  confirmed: Key[];
  isSingle: Key[];
  titleNumber: Key[];
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  setOrdering: (ordering: string) => void;
  setBroker: (broker: Key[]) => void;
  setLob: (lob: Key[]) => void;
  setEcNumber: (ecNumber: Key[]) => void;
  setEcPeriod: (ecPeriod: Key[]) => void;
  setPolicyEffectiveDate: (date: Key[]) => void;
  setPolicyExpiringDate: (date: Key[]) => void;
  setPolicyNumber: (policyNumber: Key[]) => void;
  setTitleEffectiveDate: (date: Key[]) => void;
  setTitleReceiptDate: (date: Key[]) => void;
  setCredit: (credit: Key[]) => void;
  setCreditDelta: (creditDelta: Key[]) => void;
  setCreditDifference: (creditDifference: Key[]) => void;
  setDebit: (debit: Key[]) => void;
  setDebitDelta: (debitDelta: Key[]) => void;
  setDebitDifference: (debitDifference: Key[]) => void;
  setCreditStatus: (status: Key[]) => void;
  setDebitStatus: (status: Key[]) => void;
  setCreditDebitStatus: (status: Key[]) => void;
  setContractor: (contractor: Key[]) => void;
  setConfirmed: (confirmed: Key[]) => void;
  setIsSingle: (isSingle: Key[]) => void;
  setTitleNumber: (titleNumber: Key[]) => void;
  getParamsForApiRequest: (defaultFilters?: GetStatementTitleListParams) => (GetStatementTitleListParams & PaginatedApiParams);
}

const StatementTitlesTableContext = createContext<TitlesTableContextType | undefined>(undefined);

export const StatementTitlesTableProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [ordering, setOrdering] = useState<string | undefined>(undefined);
  const [broker, setBroker] = useState<Key[]>([]);
  const [lob, setLob] = useState<Key[]>([]);
  const [ecNumber, setEcNumber] = useState<Key[]>([]);
  const [ecPeriod, setEcPeriod] = useState<Key[]>([]);
  const [policyEffectiveDate, setPolicyEffectiveDate] = useState<Key[]>([]);
  const [policyExpiringDate, setPolicyExpiringDate] = useState<Key[]>([]);
  const [policyNumber, setPolicyNumber] = useState<Key[]>([]);
  const [titleEffectiveDate, setTitleEffectiveDate] = useState<Key[]>([]);
  const [titleReceiptDate, setTitleReceiptDate] = useState<Key[]>([]);
  const [credit, setCredit] = useState<Key[]>([]);
  const [creditDelta, setCreditDelta] = useState<Key[]>([]);
  const [creditDifference, setCreditDifference] = useState<Key[]>([]);
  const [debit, setDebit] = useState<Key[]>([]);
  const [debitDelta, setDebitDelta] = useState<Key[]>([]);
  const [debitDifference, setDebitDifference] = useState<Key[]>([]);
  const [contractor, setContractor] = useState<Key[]>([]);
  const [creditStatus, setCreditStatus] = useState<Key[]>([]);
  const [debitStatus, setDebitStatus] = useState<Key[]>([]);
  const [creditDebitStatus, setCreditDebitStatus] = useState<Key[]>([]);
  const [confirmed, setConfirmed] = useState<Key[]>([]);
  const [isSingle, setIsSingle] = useState<Key[]>([]);
  const [titleNumber, setTitleNumber] = useState<Key[]>([]);

  const getParamsForApiRequest = useCallback((defaultFilters?: StatementTitleParams) => {
    return {
      page,
      page_size: pageSize,
      ...(!isEmpty(ordering) && { ordering: ordering }),
      ...(!isEmpty(defaultFilters) && { ...defaultFilters }),
      //@ts-ignore
      ...(!isEmpty(broker) && { broker: broker?.map(entry => entry.value).join(",") }),
      ...(!isEmpty(confirmed) && { confirmed: confirmed?.join(",") }),
      ...(!isEmpty(creditStatus) && { credit_status: creditStatus?.join(",") }),
      ...(!isEmpty(debitStatus) && { debit_status: debitStatus?.join(",") }),
      ...(!isEmpty(creditDebitStatus) && { credit_debit_status: creditDebitStatus?.join(",") }),
      ...(!isEmpty(lob) && { lob: lob?.join(",") }),
      ...(!isEmpty(ecNumber) && { ec_number: ecNumber?.join(",") }),
      ...(!isEmpty(contractor) && { contractor: contractor?.join(",") }),
      ...(!isEmpty(policyNumber) && { policy_number: policyNumber?.join(",") }),
      ...(!isEmpty(isSingle) && { is_single: isSingle?.join(",") }),
      ...(!isEmpty(titleNumber) && { title_number: titleNumber?.join(",") }),
      ...(!isEmpty(ecPeriod) && {
        ec_period_min: ecPeriod?.at(0) as string,
        ec_period_max: ecPeriod?.at(1) as string
      }),
      ...(!isEmpty(titleReceiptDate) && {
        title_receipt_date_min: titleReceiptDate?.at(0) as string,
        title_receipt_date_max: titleReceiptDate?.at(1) as string
      }),
      ...(!isEmpty(titleEffectiveDate) && {
        title_effective_date_min: titleEffectiveDate?.at(0) as string,
        title_effective_date_max: titleEffectiveDate?.at(1) as string
      }),
      ...(!isEmpty(policyEffectiveDate) && {
        policy_effective_date_min: policyEffectiveDate?.at(0) as string,
        policy_effective_date_max: policyEffectiveDate?.at(1) as string
      }),
      ...(!isEmpty(policyExpiringDate) && {
        policy_expiring_date_min: policyExpiringDate?.at(0) as string,
        policy_expiring_date_max: policyExpiringDate?.at(1) as string
      }),
      ...(!isEmpty(credit) && {
        credit_min: credit?.at(0) as string,
        credit_max: credit?.at(1) as string
      }),
      ...(!isEmpty(creditDelta) && {
        credit_delta_min: creditDelta?.at(0) as string,
        credit_delta_max: creditDelta?.at(1) as string
      }),
      ...(!isEmpty(debit) && {
        debit_min: debit?.at(0) as string,
        debit_max: debit?.at(1) as string
      }),
      ...(!isEmpty(debitDelta) && {
        debit_delta_min: debitDelta?.at(0) as string,
        debit_delta_max: debitDelta?.at(1) as string
      })
    };
  }, [broker, confirmed, contractor, credit, creditDebitStatus, creditDelta, creditStatus, debit, debitDelta, debitStatus, ecNumber, ecPeriod, isSingle, lob, ordering, page, pageSize, policyEffectiveDate, policyExpiringDate, policyNumber, titleEffectiveDate, titleNumber, titleReceiptDate]);

  const value: TitlesTableContextType = {
    page,
    pageSize,
    ordering,
    broker,
    lob,
    ecNumber,
    ecPeriod,
    policyEffectiveDate,
    policyExpiringDate,
    policyNumber,
    titleEffectiveDate,
    titleReceiptDate,
    credit,
    creditDelta,
    creditDifference,
    creditStatus,
    debit,
    debitDelta,
    debitDifference,
    debitStatus,
    creditDebitStatus,
    contractor,
    confirmed,
    isSingle,
    titleNumber,
    setPage,
    setPageSize,
    setOrdering,
    setBroker,
    setLob,
    setEcNumber,
    setEcPeriod,
    setPolicyEffectiveDate,
    setPolicyExpiringDate,
    setPolicyNumber,
    setTitleEffectiveDate,
    setTitleReceiptDate,
    setCredit,
    setCreditDelta,
    setCreditDifference,
    setCreditStatus,
    setDebit,
    setDebitDelta,
    setDebitStatus,
    setDebitDifference,
    setCreditDebitStatus,
    setContractor,
    setConfirmed,
    setIsSingle,
    setTitleNumber,
    getParamsForApiRequest
  };

  return (
    <StatementTitlesTableContext.Provider value={value}>
      {children}
    </StatementTitlesTableContext.Provider>
  );
};

export const useStatementsTitlesTable = (): TitlesTableContextType => {
  const context = useContext(StatementTitlesTableContext);
  if (context === undefined) {
    throw new Error("useTitlesTable deve essere utilizzato all'interno di un TitlesTableProvider");
  }
  return context;
};